import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import {
  createJobTrigger,
  CreateTriggerInput,
  deleteJobTrigger,
  getAvailableJourneys,
  getJobJourneyConfig,
  getJobTriggerCount,
  getJobTriggers,
  JobTriggerCountResponse,
  saveJobJourneyConfig,
  setJourneysBannerVisible,
  updateJobTrigger,
  UpdateTriggerInput,
} from './api';
import {
  availableJourneysQueryKey,
  availableJourneysQueryKeyPrefix,
  jobJourneyConfigQueryKey,
  jobTriggerCountQueryKey,
  jobTriggersQueryKey,
} from './query-keys';

import { journeyListStrings } from '../components/list-strings';
import { StoredFieldConfig } from '../field-config/types';
import { useHasJourneysAutomation } from '../hooks';
import { jobJourneysQueryKeyPrefix } from '../job-settings/query-keys';

// Error payloads pass through the global camelize renderer, so DRF's
// snake_case keys arrive camelCased (nonFieldErrors, journeyId, jobStage).
const getTriggerErrorMessage = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }
  const data = error.response?.data;
  return data?.nonFieldErrors?.[0] ?? data?.journeyId?.[0] ?? data?.jobStage?.[0];
};

const isNotFoundError = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 404;

// Refetch after every write, failures included: a rejected write usually means
// the list on screen no longer matches the server, so leaving it in place would
// keep showing triggers that are gone (or hiding ones that exist).
const refetchTriggers = (queryClient: QueryClient, jobId: number) => () => {
  queryClient.invalidateQueries(jobTriggersQueryKey(jobId));
  queryClient.invalidateQueries(jobTriggerCountQueryKey(jobId));
};

export const useJobTriggers = (jobId: number) => {
  return useQuery({
    queryKey: jobTriggersQueryKey(jobId),
    queryFn: () => getJobTriggers(jobId),
  });
};

type UseJobTriggerCountOptions = {
  enabled?: boolean;
};

/**
 * Trigger count for the job-header tab badge. Kept separate from
 * useJobTriggers because the badge renders on every job route and must not
 * pay for the full list (which resolves titles upstream).
 */
export const useJobTriggerCount = (jobId: number, options: UseJobTriggerCountOptions = {}) => {
  const { enabled = true } = options;
  return useQuery({
    queryKey: jobTriggerCountQueryKey(jobId),
    queryFn: () => getJobTriggerCount(jobId),
    enabled,
  });
};

export const useAvailableJourneys = (jobId: number, search: string) => {
  return useQuery({
    queryKey: availableJourneysQueryKey(jobId, search),
    queryFn: () => getAvailableJourneys(jobId, search),
    // Keep the previous search's results on screen while a new search
    // debounces in, rather than dropping to isLoading — G34 keeps the search
    // box (and the rest of the list) mounted through that gap.
    keepPreviousData: true,
  });
};

export type CreateJobTriggerVariables = Omit<CreateTriggerInput, 'jobId'> & {
  onSuccess?: () => void;
  onSettled?: () => void;
};

export const useCreateJobTrigger = (jobId: number) => {
  const { t } = useScopedTranslate(locale);
  const { showError } = useToaster();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ onSuccess, onSettled, ...input }: CreateJobTriggerVariables) =>
      createJobTrigger({ ...input, jobId }),
    onSuccess: (_data, variables) => {
      variables.onSuccess?.();
    },
    onError: (error: unknown) => {
      showError(getTriggerErrorMessage(error) ?? t('createError'));
    },
    onSettled: (_data, _error, variables) => {
      refetchTriggers(queryClient, jobId)();
      variables.onSettled?.();
    },
  });
};

export type UpdateJobTriggerVariables = Omit<UpdateTriggerInput, 'jobId'> & {
  onSettled?: () => void;
};

export const useUpdateJobTrigger = (jobId: number) => {
  const { t } = useScopedTranslate(locale);
  const { showError } = useToaster();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ onSettled, ...input }: UpdateJobTriggerVariables) =>
      updateJobTrigger({ ...input, jobId }),
    onError: (error: unknown) => {
      showError(getTriggerErrorMessage(error) ?? t('updateError'));
    },
    onSettled: (_data, _error, variables) => {
      refetchTriggers(queryClient, jobId)();
      variables.onSettled?.();
    },
  });
};

export type DeleteJobTriggerVariables = {
  triggerId: number;
  onSettled?: () => void;
};

export const useDeleteJobTrigger = (jobId: number) => {
  const { t } = useScopedTranslate(locale);
  const { showError } = useToaster();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ triggerId }: DeleteJobTriggerVariables) => {
      try {
        await deleteJobTrigger(jobId, triggerId);
      } catch (error) {
        // Someone else removed this trigger first. The state the user asked for
        // already holds, so resolve: rejecting would keep the confirmation
        // modal open offering a retry that can never succeed.
        if (!isNotFoundError(error)) {
          throw error;
        }
      }
    },
    onError: () => {
      showError(t('deleteError'));
    },
    onSettled: (_data, _error, variables) => {
      refetchTriggers(queryClient, jobId)();
      variables.onSettled?.();
    },
  });
};

// ---- Per-job journey field configuration ----

/**
 * Fetch the resolved fields + raw job override for a single journey on a job.
 * A global config save invalidates this by prefix, so no staleTime here —
 * used by the config sidebar.
 */
export const useJobJourneyConfig = (jobId: number, journeyId: string) => {
  return useQuery({
    queryKey: jobJourneyConfigQueryKey(jobId, journeyId),
    queryFn: () => getJobJourneyConfig(jobId, journeyId),
    enabled: Boolean(jobId) && Boolean(journeyId),
  });
};

export type SaveJobJourneyConfigOptions = {
  jobId: number;
  journeyId: string;
};

export type SaveJobJourneyConfigVariables = StoredFieldConfig & {
  onSuccess?: () => void;
  onSettled?: () => void;
};

export const useSaveJobJourneyConfig = ({ jobId, journeyId }: SaveJobJourneyConfigOptions) => {
  const { t } = useScopedTranslate(locale);
  const { showError } = useToaster();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ onSuccess, onSettled, ...fieldConfig }: SaveJobJourneyConfigVariables) =>
      saveJobJourneyConfig({ jobId, journeyId, fieldConfig }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(jobJourneyConfigQueryKey(jobId, journeyId));
      queryClient.invalidateQueries(jobJourneysQueryKeyPrefix(jobId));
      // The journey selector modal and the stage chip read this list too — a
      // per-job config save must not leave them showing the pre-save counts.
      // Prefix, not the full (jobId, search) key, so every cached search
      // variant gets invalidated.
      queryClient.invalidateQueries(availableJourneysQueryKeyPrefix(jobId));
      variables.onSuccess?.();
    },
    onError: () => {
      showError(t('saveConfigError'));
    },
    onSettled: (_data, _error, variables) => {
      variables.onSettled?.();
    },
  });
};

// ---- Automation banner ----

export const useDismissJourneysBanner = (jobId: number) => {
  const { t } = useScopedTranslate(locale);
  const { showError } = useToaster();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => setJourneysBannerVisible(jobId, false),
    // Hide it immediately: waiting on the round-trip leaves the banner sitting
    // there after the user has clearly asked for it to go away.
    onMutate: () => {
      queryClient.setQueryData<JobTriggerCountResponse>(jobTriggerCountQueryKey(jobId), (current) =>
        current ? { ...current, showJourneysBanner: false } : current
      );
    },
    onError: () => {
      queryClient.invalidateQueries(jobTriggerCountQueryKey(jobId));
      showError(t('dismissBannerError'));
    },
  });
};

/**
 * Whether to prompt for journey automation on this job. Owned by the page rather
 * than the banner because the pipeline measures its own height from the top of the
 * board, so it has to re-measure when this flips.
 *
 * Returns false until the answer is in, so the banner never flashes onto a job
 * that already has triggers or has already been dismissed. `showJourneysBanner`
 * is server state: default true, backfilled false for every job that existed
 * before the feature shipped, set true again on duplication.
 */
export const useShouldShowJourneysBanner = (jobId: number) => {
  const hasJourneysAutomation = useHasJourneysAutomation();
  const countQuery = useJobTriggerCount(jobId, { enabled: hasJourneysAutomation });

  if (!hasJourneysAutomation) {
    return false;
  }
  if (countQuery.data === undefined) {
    return false;
  }
  return countQuery.data.count === 0 && countQuery.data.showJourneysBanner;
};

const locale = {
  en: {
    ...journeyListStrings.en,
    createError: 'Failed to add journey trigger',
    updateError: 'Failed to update journey trigger',
    deleteError: 'Failed to remove journey trigger',
    dismissBannerError: 'Could not dismiss the banner. Please try again.',
  },
  is: {
    ...journeyListStrings.is,
    createError: 'Ekki tókst að bæta við ræsingarreglu fyrir Journey-ferli',
    updateError: 'Ekki tókst að uppfæra ræsingarreglu fyrir Journey-ferli',
    deleteError: 'Ekki tókst að fjarlægja ræsingarreglu fyrir Journey-ferli',
    dismissBannerError: 'Ekki tókst að fela upplýsingaborðann. Reyndu aftur.',
  },
};
